/**
 * WidgetSelectorSheet · v2.7.16
 * ──────────────────────────────
 * Slide-out side panel (shadcn Sheet) that lets administrators browse the
 * full widget catalog, toggle widgets on/off, and "Add to dashboard" any
 * widget not currently visible. The panel groups widgets into:
 *   · Core stats  (the original 6)
 *   · Extras      (the 8 new live widgets)
 *
 * State is owned by the parent (AdminDashboardPage) via the `activeIds`
 * + `onToggle` / `onAdd` / `onRemove` props so persistence stays at the
 * top of the tree.
 */
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, Check, LayoutGrid } from "lucide-react";
import { WIDGET_REGISTRY } from "@/components/admin/widgetLibrary";

export default function WidgetSelectorSheet({ activeIds, onToggle, triggerClassName = "" }) {
  const activeSet = new Set(activeIds || []);
  const groups = [
    { key: "stats",  label: "Core Stats",   note: "The classic 6 — drivers, revenue, trips" },
    { key: "extras", label: "Live Widgets", note: "8 new operational cards · server-fed every 30s" },
  ];

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`bg-zinc-950 border-amber-400/40 text-amber-300 hover:bg-amber-400/10 h-7 text-[11px] font-bold ${triggerClassName}`}
          data-testid="widget-selector-open"
        >
          <LayoutGrid className="h-3 w-3 mr-1.5"/>
          Add Widget
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[420px] bg-zinc-950 border-l-amber-400/30 text-zinc-100 overflow-y-auto"
        data-testid="widget-selector-sheet"
      >
        <SheetHeader>
          <SheetTitle className="text-amber-300 flex items-center gap-2">
            <LayoutGrid className="h-4 w-4"/>Widget Library
          </SheetTitle>
          <p className="text-xs text-zinc-500">
            Toggle widgets to show/hide them in the dashboard grid. Layout
            changes save automatically — drag, resize, and reorder are
            preserved across logins on this browser.
          </p>
        </SheetHeader>

        <div className="mt-4 space-y-5">
          {groups.map((g) => {
            const items = WIDGET_REGISTRY.filter((w) => w.group === g.key);
            if (items.length === 0) return null;
            return (
              <div key={g.key} data-testid={`widget-selector-group-${g.key}`}>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5">
                  {g.label} <span className="text-zinc-700 font-normal normal-case ml-1">· {g.note}</span>
                </div>
                <div className="space-y-1.5">
                  {items.map((w) => {
                    const isActive = activeSet.has(w.id);
                    const Icon = w.Icon;
                    return (
                      <Card
                        key={w.id}
                        onClick={() => onToggle(w.id)}
                        className={`p-2.5 cursor-pointer transition-all flex items-center gap-3 ${isActive
                          ? "bg-amber-400/10 border-amber-400/50 hover:bg-amber-400/15"
                          : "bg-zinc-900 border-zinc-800 hover:border-amber-400/40 hover:bg-zinc-900/80"
                        }`}
                        data-testid={`widget-selector-row-${w.id}`}
                      >
                        {Icon ? <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? "text-amber-300" : "text-zinc-400"}`}/> : null}
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs font-bold truncate ${isActive ? "text-amber-200" : "text-zinc-100"}`}>{w.label}</div>
                          <div className="text-[10px] text-zinc-500 truncate">{w.description}</div>
                        </div>
                        <div
                          className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-widest flex-shrink-0 ${isActive
                            ? "bg-amber-400 text-zinc-950"
                            : "bg-zinc-800 text-zinc-400 hover:bg-amber-400/20 hover:text-amber-300"
                          }`}
                          data-testid={`widget-selector-toggle-${w.id}`}
                        >
                          {isActive ? <><Check className="h-2.5 w-2.5"/>On</> : <><Plus className="h-2.5 w-2.5"/>Add</>}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 pt-4 border-t border-zinc-800 text-[10px] text-zinc-500 leading-relaxed">
          <strong className="text-amber-300">Tip:</strong> Drag any widget to reorder, hover for the
          S/M/L/XL resize pills, and click <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-zinc-300">Customize</kbd>
          in the dashboard toolbar to fine-tune visibility + sizes in bulk.
        </div>
      </SheetContent>
    </Sheet>
  );
}
